import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../../config/config');

export const RELATIONSHIP_OPTIONS = [
    { value: 'SPOUSE', label: 'Spouse' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'SIBLING', label: 'Sibling' },
    { value: 'CHILD', label: 'Child' },
    { value: 'RELATIVE', label: 'Other Relative' },
    { value: 'FRIEND', label: 'Friend' },
    { value: 'OTHER', label: 'Other' }
];

const EMPTY_FORM = {
    name: '',
    relationship: '',
    phone: '',
    alt_phone: '',
    id_no: '',
    email: '',
    physical_address: '',
    is_primary: false,
    is_guarantor: false,
    notes: ''
};

const KENYAN_MOBILE_RE = /^0[17]\d{8}$/;

const inputClass = (hasError) =>
    `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none transition-all ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;

const NextOfKinModal = ({ open, onClose, driver, existing, isFirst, onSaved }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setError('');
        if (existing) {
            const values = {};
            Object.keys(EMPTY_FORM).forEach((key) => {
                values[key] = existing[key] ?? EMPTY_FORM[key];
            });
            setForm(values);
        } else {
            setForm({ ...EMPTY_FORM, is_primary: !!isFirst });
        }
    }, [open, existing, isFirst]);

    if (!open) return null;

    const isEdit = !!existing;
    // The current primary can only be replaced by marking another contact primary
    const primaryLocked = isFirst || (isEdit && existing.is_primary);

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const uppercased = ['name', 'id_no'].includes(name) ? value.toUpperCase() : value;
        setField(name, uppercased);
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Enter the next of kin full name';
        if (!form.relationship) e.relationship = 'Select how this person is related to the driver';
        if (!KENYAN_MOBILE_RE.test(form.phone)) e.phone = 'Enter a 10-digit mobile number starting with 07 or 01, e.g. 0712345678';
        else if (driver?.phone && form.phone === driver.phone) e.phone = "This is the driver's own phone number";
        if (form.alt_phone) {
            if (!KENYAN_MOBILE_RE.test(form.alt_phone)) e.alt_phone = 'Enter a 10-digit mobile number starting with 07 or 01';
            else if (form.alt_phone === form.phone) e.alt_phone = 'Alternative phone must differ from the main phone';
            else if (driver?.phone && form.alt_phone === driver.phone) e.alt_phone = "This is the driver's own phone number";
        }
        if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
        if (form.id_no && driver?.id_no && form.id_no === String(driver.id_no).toUpperCase()) e.id_no = "This is the driver's own ID number";
        setErrors(e);
        if (Object.keys(e).length > 0) setError('Please fix the highlighted fields before saving.');
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validate()) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('authToken') || '';
            const base = `${rootUrl.replace(/\/$/, '')}/drivers/${driver.id}/next-of-kin`;
            const res = await fetch(isEdit ? `${base}/${existing.id}` : base, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save next of kin');
            if (onSaved) onSaved(data.data);
            onClose();
        } catch (err) {
            setError(err.message || 'An error occurred while saving.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="bg-gradient-to-r from-[#FF5733] to-[#FF8C66] px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">{isEdit ? 'Edit Next of Kin' : 'Add Next of Kin'}</h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-3 text-red-700 text-sm rounded">{error}</div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                            <input type="text" name="name" value={form.name} onChange={handleChange} className={inputClass(errors.name)} />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select relationship"
                                options={RELATIONSHIP_OPTIONS}
                                value={RELATIONSHIP_OPTIONS.find((o) => o.value === form.relationship) || null}
                                onChange={(option) => setField('relationship', option?.value || '')}
                            />
                            {errors.relationship && <p className="text-red-500 text-xs mt-1">{errors.relationship}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                            <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="0712345678" maxLength={10} className={inputClass(errors.phone)} />
                            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Alternative Phone</label>
                            <input type="tel" name="alt_phone" value={form.alt_phone} onChange={handleChange} maxLength={10} className={inputClass(errors.alt_phone)} />
                            {errors.alt_phone && <p className="text-red-500 text-xs mt-1">{errors.alt_phone}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                            <input type="text" name="id_no" value={form.id_no} onChange={handleChange} className={inputClass(errors.id_no)} />
                            {errors.id_no && <p className="text-red-500 text-xs mt-1">{errors.id_no}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input type="email" name="email" value={form.email} onChange={handleChange} className={inputClass(errors.email)} />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                        <input type="text" name="physical_address" value={form.physical_address} onChange={handleChange} placeholder="Estate, town, landmark" className={inputClass(false)} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea name="notes" value={form.notes} onChange={handleChange} rows="3" className={`${inputClass(false)} resize-none`} />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <label className={`flex items-center gap-2 text-sm text-gray-700 ${primaryLocked ? 'opacity-60' : 'cursor-pointer'}`}>
                            <input
                                type="checkbox"
                                checked={form.is_primary}
                                disabled={primaryLocked}
                                onChange={(e) => setField('is_primary', e.target.checked)}
                                className="accent-[#FF5733]"
                            />
                            Primary contact
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.is_guarantor}
                                onChange={(e) => setField('is_guarantor', e.target.checked)}
                                className="accent-[#FF5733]"
                            />
                            Guarantor
                        </label>
                    </div>
                    {primaryLocked && (
                        <p className="text-xs text-gray-500">
                            {isFirst ? 'The first next of kin is always the primary contact.' : 'To change the primary contact, mark another next of kin as primary.'}
                        </p>
                    )}

                    <div className="flex justify-end space-x-3 pt-2">
                        <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={submitting} className="px-6 py-2 bg-[#FF5733] hover:bg-[#E64A19] text-white font-medium rounded-lg shadow-md transition-all flex items-center">
                            {submitting ? (<><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Saving...</>) : (isEdit ? 'Save Changes' : 'Add Next of Kin')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NextOfKinModal;
